import { keepPreviousSearchData } from "@/lib/data/react-query/search/scope";
import { invalidateSearchQueries } from "@/lib/data/react-query/search/invalidation";
import {
  ActionEmptyState,
  AppPageShell,
  Badge,
  Button,
  Card,
  GlobalNotAuthorized,
  Input,
  LoadingSpinner,
  IncidentTypeBadge,
  Typography,
} from "@/components/ui";
import {
  buildMatterStatusGroups,
  LeadStatusTransitionDropdown,
} from "@/components/features/leads/LeadStatusTransitionDropdown";
import { LeadEditAside } from "@/components/features/leads/LeadEditAside";
import { LeadRowContextMenu } from "@/components/features/leads/LeadRowContextMenu";
import { LeadsAssigneeSummary } from "@/components/features/leads/LeadsAssigneeSummary";
import { useUser } from "@/components/UserProvider";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { searchMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import {
  getSearchMattersCount,
  getSearchMattersNextPageNumber,
  type SearchMatterStatusCategory,
} from "@/lib/data/requests";
import {
  ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS,
  LEAD_STATUS_CATEGORY_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { getIncidentTypeBadgeVariantFromId } from "@/components/ui/MatterTypeBadge/MatterTypeBadge";
import { cn } from "@/lib/utils/cn";
import { formatDate } from "@/lib/utils/format";
import {
  getMatterSearchResultAssigneeDisplayNames,
  getMatterSearchResultClientName,
} from "@/lib/utils/matters/matter-search-result-utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/_auth/leads/")({
  component: RouteComponent,
});

const LEADS_SEARCH_DEBOUNCE_MS = 300;
const LEADS_PAGE_SIZE = 25;

function RouteComponent() {
  const { hasPagePermission, hasActionPermission } = useUser();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const canViewLeads = hasPagePermission(PAGE_PERMISSION_KEYS.intakeLeads);
  const debouncedSearchValue = useDebouncedValue(
    searchValue,
    LEADS_SEARCH_DEBOUNCE_MS,
  );
  const matterSearchFilters = useMemo(() => {
    const search = debouncedSearchValue.trim();
    return {
      statusCategories: ["lead"] as SearchMatterStatusCategory[],
      ...(search ? { search } : {}),
      ...(showUnassignedOnly ? { unassigned: true } : {}),
    };
  }, [debouncedSearchValue, showUnassignedOnly]);
  const matterSearchSignature = useMemo(
    () => JSON.stringify(matterSearchFilters),
    [matterSearchFilters],
  );

  useEffect(() => {
    setPageIndex(0);
  }, [matterSearchSignature]);

  const {
    data: leadsData,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    ...searchMattersQueryOptions({
      ...matterSearchFilters,
      pageNumber: pageIndex + 1,
      pageSize: LEADS_PAGE_SIZE,
    }),
    enabled: canViewLeads,
    placeholderData: keepPreviousSearchData,
  });
  const nextPageNumber = getSearchMattersNextPageNumber(leadsData);
  const totalCount = getSearchMattersCount(leadsData);
  const pageCount =
    typeof totalCount === "number"
      ? Math.max(1, Math.ceil(totalCount / LEADS_PAGE_SIZE))
      : Math.max(1, nextPageNumber ? pageIndex + 2 : pageIndex + 1);
  const canGoNext = Boolean(nextPageNumber);
  const canGoPrevious = pageIndex > 0;
  const rangeStart = leadsData?.data.length
    ? pageIndex * LEADS_PAGE_SIZE + 1
    : 0;
  const rangeEnd = pageIndex * LEADS_PAGE_SIZE + (leadsData?.data.length ?? 0);
  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewLeads,
  });
  const matterStatuses = useMemo(
    () => matterSettingsData?.data.statuses ?? [],
    [matterSettingsData],
  );
  const leadMatterStatuses = useMemo(
    () =>
      matterStatuses.filter((status) =>
        LEAD_STATUS_CATEGORY_OPTIONS.includes(
          status.categoryId as (typeof LEAD_STATUS_CATEGORY_OPTIONS)[number],
        ),
      ),
    [matterStatuses],
  );
  const incidentTypes = useMemo(
    () => matterSettingsData?.data.incidentTypes ?? [],
    [matterSettingsData],
  );
  const matterRoles = useMemo(
    () => matterSettingsData?.data.roles ?? [],
    [matterSettingsData],
  );
  const matterTeams = useMemo(
    () => matterSettingsData?.data.teams ?? [],
    [matterSettingsData],
  );
  const matterStatusGroups = useMemo(
    () => buildMatterStatusGroups(leadMatterStatuses),
    [leadMatterStatuses],
  );
  const conversionMatterStatusGroups = useMemo(
    () =>
      buildMatterStatusGroups(
        matterStatuses.filter((status) =>
          ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS.includes(
            status.categoryId as (typeof ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS)[number],
          ),
        ),
      ),
    [matterStatuses],
  );
  const canTransitionMatterStatus = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatusPhase,
    ACTION_PERMISSION_VALUES.create,
  );

  const refreshLeadsListData = useCallback(() => {
    void invalidateSearchQueries(queryClient);
  }, [queryClient]);

  const goToPreviousPage = () => {
    if (!canGoPrevious) {
      return;
    }

    setPageIndex((current) => current - 1);
  };

  const goToNextPage = () => {
    if (!nextPageNumber) {
      return;
    }

    setPageIndex((current) => current + 1);
  };

  const leads = leadsData?.data ?? [];
  const hasActiveListFilters =
    debouncedSearchValue.trim().length > 0 || showUnassignedOnly;
  const selectedLead = useMemo(() => {
    if (!selectedLeadId) {
      return undefined;
    }

    return (leadsData?.data ?? []).find((lead) => lead.id === selectedLeadId);
  }, [leadsData, selectedLeadId]);
  const isLeadAsideOpen = isCreatingLead || !!selectedLead;
  const leadAsideKey = isCreatingLead
    ? "new-lead"
    : (selectedLead?.id ?? "closed-lead-aside");

  if (!canViewLeads) {
    return <GlobalNotAuthorized />;
  }

  return (
    <AppPageShell className="h-full max-h-full gap-6 overflow-hidden">
      <div className="shrink-0 space-y-1">
        <Typography variant="heading" className="text-2xl">
          Leads
        </Typography>
      </div>

      <Card className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 flex-1">
          <div
            className={cn(
              "flex min-w-0 shrink-0 grow-0 flex-col transition-[flex-basis] duration-300 ease-in-out",
              isLeadAsideOpen ? "basis-full md:basis-1/2" : "basis-full",
            )}
          >
            <div className="flex shrink-0 flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
              <div className="relative w-full md:max-w-xs">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  maxLength={200}
                  value={searchValue}
                  onChange={(event) => {
                    setSearchValue(event.target.value);
                  }}
                  placeholder="Search leads..."
                  className="bg-background h-9 pl-9"
                />
              </div>
              <Button
                size="sm"
                className="self-start md:self-auto"
                onClick={() => {
                  setSelectedLeadId(null);
                  setIsCreatingLead(true);
                }}
              >
                <Plus className="h-4 w-4" />
                New lead
              </Button>
            </div>

            <div className="flex shrink-0 gap-2 border-b p-4">
              <Button
                size="xs"
                variant={showUnassignedOnly ? "outline" : "default"}
                className="rounded-full px-3"
                onClick={() => {
                  setShowUnassignedOnly(false);
                }}
              >
                All
              </Button>
              <Button
                size="xs"
                variant={showUnassignedOnly ? "default" : "outline"}
                className="rounded-full px-3"
                onClick={() => {
                  setShowUnassignedOnly(true);
                }}
              >
                Unassigned
              </Button>
            </div>

            <div className="relative min-h-0 flex-1 divide-y overflow-y-auto">
              {isLoading ? (
                <div className="text-muted-foreground flex items-center gap-2 px-4 py-8 text-sm">
                  <LoadingSpinner />
                  Loading leads...
                </div>
              ) : isError ? (
                <div className="px-4 py-8">
                  <Typography className="text-destructive text-sm">
                    Unable to load leads.
                  </Typography>
                </div>
              ) : leads.length === 0 ? (
                <div className="flex min-h-full w-full items-center justify-center">
                  <ActionEmptyState
                    variant="plain"
                    title={
                      hasActiveListFilters ? "No leads found" : "No leads yet"
                    }
                    description={
                      hasActiveListFilters
                        ? "Try adjusting or clearing your search and filters."
                        : "Create a lead to start tracking a potential new matter."
                    }
                    actions={
                      hasActiveListFilters ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSearchValue("");
                            setShowUnassignedOnly(false);
                          }}
                        >
                          Clear filters
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedLeadId(null);
                            setIsCreatingLead(true);
                          }}
                        >
                          <Plus className="h-4 w-4" />
                          New lead
                        </Button>
                      )
                    }
                  />
                </div>
              ) : (
                leads.map((lead) => {
                  const matterId = lead.id;
                  const assigneeDisplayNames =
                    getMatterSearchResultAssigneeDisplayNames(lead);
                  const leadName =
                    getMatterSearchResultClientName(lead) ||
                    lead.title ||
                    "Untitled lead";
                  const incidentType = lead.incidentType?.label || "Lead";
                  const statusLabel = lead.status?.label || "Active";
                  const statusId =
                    lead.status?.id ?? lead.statusId ?? undefined;
                  const isSelected = selectedLeadId === lead.id;

                  return (
                    <LeadRowContextMenu
                      key={lead.id}
                      lead={lead}
                      matterRoles={matterRoles}
                      matterStatuses={leadMatterStatuses}
                      isMatterStatusesError={isMatterSettingsError}
                      isMatterStatusesLoading={isMatterSettingsLoading}
                      trigger={
                        <div
                          className={cn(
                            "hover:bg-accent hover:text-accent-foreground flex cursor-pointer items-center gap-4 px-4 py-4 transition-colors",
                            isSelected &&
                              "border-primary bg-primary/5 border-l-2 pl-3.5",
                          )}
                          onClick={() => {
                            setSelectedLeadId(lead.id);
                            setIsCreatingLead(false);
                          }}
                        >
                          <div className="border-border bg-background h-5 w-5 shrink-0 rounded border" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <Typography weight="medium" className="truncate">
                                {leadName}
                              </Typography>
                              <IncidentTypeBadge
                                variant={getIncidentTypeBadgeVariantFromId(
                                  incidentType,
                                )}
                                className="px-2 py-0 text-[11px]"
                              >
                                {incidentType}
                              </IncidentTypeBadge>
                              {lead.claimTypes?.length ? (
                                <span className="text-muted-foreground text-xs">
                                  Claims:{" "}
                                  {lead.claimTypes
                                    .map((claim) => claim.label)
                                    .join(", ")}
                                </span>
                              ) : null}
                              <LeadsAssigneeSummary
                                names={assigneeDisplayNames}
                              />
                            </div>
                            <Typography variant="subHeading">
                              {formatDate(lead.dateCreated) ?? "No date"}
                            </Typography>
                          </div>
                          <div
                            className={cn(
                              "items-center gap-3 text-sm",
                              isLeadAsideOpen
                                ? "hidden xl:flex"
                                : "hidden md:flex",
                            )}
                          >
                            <Typography className="text-muted-foreground">
                              {formatDate(lead.dateModified) ?? ""}
                            </Typography>
                            {matterId ? (
                              <LeadStatusTransitionDropdown
                                matterId={matterId}
                                currentStatusId={statusId}
                                currentStatusLabel={statusLabel}
                                matterStatusGroups={matterStatusGroups}
                                canTransitionMatterStatus={
                                  canTransitionMatterStatus
                                }
                                isItemsLoading={isMatterSettingsLoading}
                                isItemsError={isMatterSettingsError}
                              />
                            ) : (
                              <Badge
                                variant="outline"
                                className="border-blue-200 bg-blue-50 text-blue-700"
                              >
                                {statusLabel}
                              </Badge>
                            )}
                          </div>
                        </div>
                      }
                    />
                  );
                })
              )}
            </div>
            <div className="flex shrink-0 flex-col gap-2 border-t px-4 py-3 text-sm md:flex-row md:items-center md:justify-between">
              <Typography className="text-muted-foreground text-sm">
                Showing {rangeStart}-{rangeEnd} of{" "}
                {typeof totalCount === "number" ? totalCount : "unknown"} leads
                {isFetching && !isLoading ? " (updating...)" : ""}
              </Typography>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={goToPreviousPage}
                  disabled={!canGoPrevious || isFetching}
                >
                  Previous
                </Button>
                <Typography className="text-muted-foreground text-sm">
                  Page {pageIndex + 1} of {pageCount}
                </Typography>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={goToNextPage}
                  disabled={!canGoNext || isFetching}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>

          <LeadEditAside
            key={leadAsideKey}
            isOpen={isLeadAsideOpen}
            selectedLead={selectedLead}
            matterStatuses={matterStatuses}
            matterStatusGroups={matterStatusGroups}
            conversionMatterStatusGroups={conversionMatterStatusGroups}
            conversionTarget="matter"
            matterRoles={matterRoles}
            matterTeams={matterTeams}
            incidentTypes={incidentTypes}
            canTransitionMatterStatus={canTransitionMatterStatus}
            isMatterSettingsLoading={isMatterSettingsLoading}
            isMatterSettingsError={isMatterSettingsError}
            onClose={() => {
              setSelectedLeadId(null);
              setIsCreatingLead(false);
            }}
            refreshLeadsListData={refreshLeadsListData}
          />
        </div>
      </Card>
    </AppPageShell>
  );
}
